import { Alert, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { RecordScreen } from '../../components/RecordScreen';
import { useIslandToday, useLocationId } from '../../hooks/useSeedLocation';
import { enumColor, enumLabel, enumOptions } from '../../lib/enums';
import { formatDate, formatDuration } from '../../lib/format';
import { PersonDetailsPanel, type StaffMember } from './PersonDetailsPanel';
import { openPinResetModal } from './PinResetModal';
import { payPhrase } from './pay';
import { RolesPanel } from './RolesPanel';
import { SchedulePanel } from './SchedulePanel';

interface Compensation {
  id: string;
  staffId: string;
  type: string;
  amountMinor: number;
  currency: string;
  positionTitle?: string | null;
  effectiveFrom: string;
}

/**
 * One person, and everything about them working here — including changing it.
 *
 * This screen exists because the portal previously could only render collections: to answer
 * "what do we pay McKenzie, and since when?" you read a name in one table, then carried a
 * 26-character identifier to a different section of the app and matched it by eye.
 *
 * It now also OWNS the person. Their details used to be editable from a dialog on the staff list
 * while everything else about them lived here, so the same phone number had two places to be
 * changed and no way to tell which one a manager had used — and the one fact nobody could set
 * anywhere, their role, is the fact that decides what the register lets them do. All of it is here,
 * edited in place, section by section.
 */
export function StaffDetailPage() {
  const { staffId = '' } = useParams();
  const locationId = useLocationId();
  const queryClient = useQueryClient();

  // The list endpoint already returns every field this page needs, so it is reused rather than
  // adding a fetch-by-id the contract does not have. It is also usually warm from the staff list.
  const staffQuery = useQuery({
    queryKey: listKey('/admin/v1/staff/members'),
    queryFn: () => rawApi<StaffMember[]>('GET', '/admin/v1/staff/members'),
  });
  const member = (staffQuery.data ?? []).find((s) => s.id === staffId);

  const payQuery = useQuery({
    queryKey: listKey('/admin/v1/payroll/compensations', { staffId }),
    queryFn: () => rawApi<Compensation[]>('GET', `/admin/v1/payroll/compensations?staffId=${staffId}`),
    enabled: Boolean(staffId),
  });

  const history = [...(payQuery.data ?? [])].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1));
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const today = useIslandToday();
  // "Current" is the most recent record that has actually taken effect — a raise dated next month
  // is real and worth showing, but it is not what this person is paid today.
  const current = history.find((c) => c.effectiveFrom <= today);
  const upcoming = history.filter((c) => c.effectiveFrom > today);
  const nextChange = upcoming.at(-1);
  const tenure = member?.hiredAt ? formatDuration(member.hiredAt, member.terminatedAt) : null;

  return (
    <RecordScreen
      backTo="/staff"
      backLabel="All staff"
      isLoading={staffQuery.isLoading}
      /*
       * This page finds its person by searching the list it fetched, so "the request failed" and
       * "they are not in it" arrive as the same empty answer. Told apart, because announcing that
       * Yolany is no longer on file — when she is, and a fetch merely timed out — is a sentence the
       * owner would act on.
       */
      loadFailed={staffQuery.isError && !staffQuery.data}
      loadFailedMessage="This person's record could not be read just now. Nothing has changed. Refresh the page to try again."
      notFound={!staffQuery.isLoading && !staffQuery.isError && !member}
      notFoundMessage="This person is no longer on file."
      title={member?.displayName ?? ''}
      subtitle={current?.positionTitle ?? undefined}
      status={
        member
          ? { label: enumLabel('staffStatus', member.status), color: enumColor('staffStatus', member.status) }
          : undefined
      }
      actions={
        member ? (
          <Button
            variant="light"
            onClick={() =>
              openPinResetModal(member.id, member.displayName, () => {
                void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/staff/members') });
              })
            }
          >
            Set register PIN
          </Button>
        ) : undefined
      }
      /*
       * Deliberately only the facts that are worked out from other records rather than typed.
       * Everything a manager can change sits in the section below, so that a value is never shown
       * twice with only one of the two being editable — the arrangement that had people correcting
       * a phone number in the place that did not save it.
       */
      facts={
        member
          ? [
              {
                // One fact, not two. "Paid L 16,000" over "Basis: Monthly salary" is the
                // compensations table wearing a hat: L 16,000 an hour and L 16,000 a month are
                // different by a factor of about two hundred, and the reader had to pair two
                // labels to find out which one they were looking at.
                label: 'Paid',
                value: current ? payPhrase(current.amountMinor, current.currency, current.type) : null,
                empty: 'No rate set',
              },
              {
                label: 'With you',
                value: tenure,
                empty: 'No start date',
              },
            ]
          : []
      }
      sections={
        member
          ? [
              {
                title: 'Details',
                description: 'Who they are, how to reach them, and whether they still work here.',
                children: <PersonDetailsPanel member={member} />,
              },
              {
                title: 'Register access',
                description: 'The PIN this person types to sign in at a register.',
                children: member.pinUpdatedAt ? (
                  <Group gap="xs">
                    <Text size="sm">PIN last set {formatDate(member.pinUpdatedAt)}.</Text>
                    {/*
                      STATES THE CONDITION, NOT A FUTURE EVENT. This read "They must choose a new
                      one at first sign-in", and for a long time nothing made them: the register
                      never asked, so the badge sat yellow for the whole of somebody's employment
                      while promising a change that was already overdue on the day it appeared. The
                      wording still describes the condition rather than the promise, and it should
                      stay that way — what the flag records is that the digits came off the reset
                      screen and have not been replaced. The register does now refuse everything
                      until they are, which is what makes the badge short-lived rather than
                      permanent; it is not what the badge is claiming.
                    */}
                    {member.mustChangePin && (
                      <Badge color="yellow" variant="light">
                        Still on the PIN you set
                      </Badge>
                    )}
                  </Group>
                ) : (
                  <Alert color="yellow" variant="light" title="No PIN yet">
                    {member.displayName} cannot sign in at a register until you set one.
                  </Alert>
                ),
              },
              {
                title: 'Working week',
                description:
                  'The days and hours they are expected to work. Approved hours are measured against this.',
                children: <SchedulePanel staffId={member.id} staffName={member.displayName} />,
              },
              {
                title: 'Roles',
                // Says WHERE a role applies, because the answer is not this site. Staff have no
                // accounts here and never will; a role decides what the register, the kitchen screen
                // and the stock count allow once they have typed their PIN.
                description:
                  'The jobs this person does, and what those jobs let them do when they sign in at a register with their PIN.',
                children: <RolesPanel staffId={member.id} staffName={member.displayName} />,
              },
            ]
          : []
      }
    >
      {/* Pay lives on the person, not in a separate corner of the app keyed by an identifier. */}
      <Stack gap={0}>
        {member && member.status === 'active' && !current && (
          <Alert color="yellow" variant="light" title="No pay rate set" mb="md">
            {member.displayName} will be left out of the payroll register until a rate is recorded.
          </Alert>
        )}
        {/* `upcoming` is newest-first, so the last entry is the change that takes effect soonest. */}
        {nextChange && current && (
          <Alert color="blue" variant="light" mb="md" title="A change is scheduled">
            {payPhrase(nextChange.amountMinor, nextChange.currency, nextChange.type)} from{' '}
            {formatDate(nextChange.effectiveFrom)}.
          </Alert>
        )}
        <CrudSection
          title="Pay history"
          entityNoun="pay change"
          description="Each change is a new dated record, so past payroll always reflects the rate in force at the time."
          listPath="/admin/v1/payroll/compensations"
          listQuery={{ staffId }}
          defaults={{ staffId }}
          emptyMessage="No pay recorded yet."
          rowLabel={() => member?.displayName ?? 'this person'}
          columns={[
            { accessor: 'effectiveFrom', title: 'From', unit: 'date' },
            {
              accessor: 'amountMinor',
              title: 'Paid',
              noWrap: true,
              render: (r) => payPhrase(Number(r.amountMinor), String(r.currency ?? 'HNL'), r.type as string),
            },
            { accessor: 'positionTitle', title: 'Position' },
            {
              // The one term of employment on this record that costs money and cannot be seen
              // anywhere else. It decides whether extra hours are owed a premium, it is off by
              // default, and the register only mentions it when it is too late — by naming the
              // person it could not price. "Yes"/blank rather than a badge on every row: the
              // answer is no for most of the staff, and a column of grey "No" reads as a problem.
              accessor: 'overtimeEligible',
              title: 'Overtime premium',
              noWrap: true,
              render: (r) => (r.overtimeEligible ? 'Paid at a premium' : ''),
            },
          ]}
          fields={[
            {
              name: 'type',
              label: 'How they are paid',
              type: 'select',
              required: true,
              options: enumOptions('compensationType'),
            },
            {
              name: 'amountMinor',
              label: 'Amount',
              type: 'number',
              required: true,
              unit: 'money',
              description: 'Per month for a salary, or per hour for hourly pay',
            },
            {
              name: 'currency',
              label: 'Currency',
              type: 'select',
              required: true,
              options: enumOptions('currency'),
              description: 'Which money this amount is in — the register and the register both use it',
            },
            {
              name: 'overtimeEligible',
              label: 'Overtime is paid at a premium',
              type: 'boolean',
              // Explicit, and off by default, matching the column. Whether extra hours carry a
              // premium is a term of employment, not something to infer from salaried-vs-hourly.
              defaultValue: false,
              description: 'Leave off if extra hours are already covered by what they are paid',
            },
            { name: 'positionTitle', label: 'Position', type: 'text', nullable: true },
            {
              name: 'effectiveFrom',
              label: 'Effective from',
              type: 'date',
              required: true,
              description: 'Payroll before this date keeps using the previous rate',
            },
          ]}
        />

        {/*
          Corrections happen here, on the person, because that is where the manager already is.
          The hours screen flags "a shift was never clocked out" and offers a link to this page; it
          used to arrive at a read-only list, and the only way to actually add the missing punch was
          a separate screen listing five hundred punches for everybody, where the first thing asked
          was which person you meant.
        */}
        <CrudSection
          title="Recent hours"
          entityNoun="correction"
          description="Clock-ins, clock-outs and breaks recorded at the register. Adding a missed punch needs a reason and is kept against your name."
          listPath="/admin/v1/payroll/time-clock"
          listQuery={{ staffId }}
          createPath="/admin/v1/payroll/time-clock/adjust"
          defaults={{ staffId, locationId }}
          emptyMessage="No hours recorded yet."
          rowLabel={() => `${member?.displayName ?? 'this person'}'s hours`}
          columns={[
            { accessor: 'at', title: 'When', unit: 'datetime' },
            { accessor: 'kind', title: 'Event', enumGroup: 'timeClockKind' },
            {
              accessor: 'editReason',
              title: 'Correction',
              render: (r) =>
                r.editReason ? (
                  <Text size="xs">
                    {String(r.editReason)}
                    {r.editedByName ? ` — ${String(r.editedByName)}` : ''}
                  </Text>
                ) : null,
            },
          ]}
          fields={[
            {
              name: 'kind',
              label: 'What happened',
              type: 'select',
              required: true,
              options: enumOptions('timeClockKind'),
            },
            { name: 'at', label: 'When', type: 'datetime', required: true },
            {
              name: 'breakPaid',
              label: 'This break is paid',
              type: 'boolean',
              defaultValue: false,
              description: 'Breaks are unpaid unless you tick this. Ignored on a clock-in or clock-out.',
            },
            {
              name: 'editReason',
              label: 'Reason for the correction',
              type: 'text',
              required: true,
              description: 'Kept with the record so the change can be explained later',
            },
          ]}
        />
      </Stack>
    </RecordScreen>
  );
}
